import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../../css/DesignSystem/Dropdowns/CheckboxMultiSelect.css";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";

const CheckboxMultiSelect = ({
  label,
  required = false,
  value = [],
  options = [],
  placeholder = "Select options",
  helperText = "Select all relevant options",
  disabled = false,
  searchable = true,
  searchPlaceholder = "Type to filter...",
  onChange,
  onOpen,
  onDone,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedLabels = useMemo(() => {
    return options
      .filter((option) => value.includes(option.value))
      .map((option) => option.label);
  }, [options, value]);

  const displayValue = selectedLabels.length
    ? selectedLabels.join(", ")
    : placeholder;

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return options;

    return options.filter((option) => {
      return `${option.label || ""} ${option.value || ""}`
        .toLowerCase()
        .includes(term);
    });
  }, [options, searchTerm]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen, searchable]);

  const toggleDropdown = () => {
    if (disabled) return;
    if (!isOpen) onOpen?.();
    setIsOpen((prev) => {
      if (prev) setSearchTerm("");
      return !prev;
    });
  };

  const toggleOption = (optionValue) => {
    if (disabled) return;

    const alreadySelected = value.includes(optionValue);

    const nextValue = alreadySelected
      ? value.filter((item) => item !== optionValue)
      : [...value, optionValue];

    onChange(nextValue);
  };

  const clearAll = (event) => {
    event.stopPropagation();
    onChange([]);
  };

  const closeDropdown = () => {
    onDone?.();
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      ref={dropdownRef}
      className={`fp-form-column CheckboxMultiSelect ${
        disabled ? "CheckboxMultiSelect--Disabled" : ""
      }`}
    >
      {label && (
        <span className="FP_Content">
          {label}
          {required && <span className="fp-form-required">*</span>}
        </span>
      )}

      <button
        type="button"
        className={`CheckboxMultiSelect__Trigger ${
          isOpen ? "CheckboxMultiSelect__Trigger--Open" : ""
        }`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span
          className={
            selectedLabels.length
              ? "CheckboxMultiSelect__Value FP_Content"
              : "CheckboxMultiSelect__Placeholder FP_Content"
          }
        >
          {displayValue}
        </span>

        <span className="CheckboxMultiSelect__Right">
          {!!selectedLabels.length && (
            <span className="CheckboxMultiSelect__Count FP_Help_Text_Semibold">
              {selectedLabels.length}
            </span>
          )}

          <span
            className={`CheckboxMultiSelect__Arrow ${
              isOpen ? "CheckboxMultiSelect__Arrow--Open" : ""
            }`}
            aria-hidden="true"
          >
            <ArrowDownIcon />
          </span>
        </span>
      </button>

      {isOpen && (
        <div className="CheckboxMultiSelect__Menu">
          <div className="CheckboxMultiSelect__MenuHeader">
            <span className="FP_Help_Text">{helperText}</span>

            {!!selectedLabels.length && (
              <button type="button" onClick={clearAll}>
                <span className="FP_Help_Text_Semibold">Clear</span>
              </button>
            )}
          </div>

          {searchable && (
            <div
              className="CheckboxMultiSelect__SearchWrap"
              onClick={(event) => event.stopPropagation()}
            >
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={searchPlaceholder}
                className="CheckboxMultiSelect__Search FP_Content"
              />
            </div>
          )}

          <div className="CheckboxMultiSelect__Options FP_Content_Semibold">
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <label key={option.value} className="CheckboxMultiSelect__Option">
                  <input
                    checked={value.includes(option.value)}
                    type="checkbox"
                    onChange={() => toggleOption(option.value)}
                  />

                  <span>{option.label}</span>
                </label>
              ))
            ) : (
              <div className="CheckboxMultiSelect__Empty FP_Content">
                No results found
              </div>
            )}
          </div>

          <div className="CheckboxMultiSelect__Footer">
            <button type="button" onClick={closeDropdown}>
              <span className="FP_Help_Text_Semibold">Done</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckboxMultiSelect;
